import React, { useEffect, useContext, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import { taskListColumns } from "./columns";

import TimesheetsDialog from "./TimesheetsDialog";
import {
  useCreateTask,
  useDeleteTask,
  useGetTasks,
  useUpdateTask,
  useArchiveTask,
} from "./hooks";
import { sidebarWidth, validateTask } from "./utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../../../../../../context/authContext";
import { useMsal } from "@azure/msal-react";
import {
  Skeleton,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import dayjs from "dayjs";

import { EditableTable } from "../../../../../../components/Tables/NewEditableTable";
import CommentsDialog from "../../../../../../components/CommentsDialog";
import ColoredSnackbar from "../../../../../../components/ColoredSnakebar";

// This will be set dynamically based on fixedType prop
const getInitialData = (fixedType) => ({
  task: "",
  taskCode: "",
  type: fixedType || "",
  task_members: [],
  priority: "",
  targetDate: "",
  hours: 0,
  progress: 0,
  status: "",
  comments: [],
  attachment: "",
  isNew: true,
});

export default function TaskList({ selectedPlant, type: fixedType, mrq }) {
  console.log("selectedPlant", selectedPlant, "fixedType", fixedType, "mrq", mrq);

  const queryClient = useQueryClient();
  const { user, accessToken } = useContext(AuthContext);
  const role = user?.role;
  const { accounts } = useMsal();
  const username = accounts[0]?.name;

  const initialDialogState = {
    comments: false,
    attachment: false,
    timesheet: false,
  };

  const [dialogState, setDialogState] = useState(initialDialogState);
  const [selectedTask, setSelectedTask] = useState();
  const [filterType, setFilterType] = useState("");
  const [commentsModalState, setCommentsDialogState] = useState(false);
  const [clickedProject, setClickedProject] = useState("");
  const [currentRole, setCurrentRole] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { data: tasksData, isLoading: tasksLoading } = useGetTasks({
    accessToken,
  });
  const { mutateAsync: archiveTaskMutation } = useArchiveTask();

  // Derive total logged hours for each task from nested user timesheets
  const tasksWithHours = useMemo(() => {
    if (!Array.isArray(tasksData)) return [];

    return tasksData.map((task) => {
      const totalHours = (task.users || []).reduce((taskAcc, user) => {
        const userHours = (user.timesheets || []).reduce((userAcc, ts) => {
          // Ensure timesheet belongs to this task and hours is numeric
          if (ts.taskId !== task.id) return userAcc;
          const hoursValue = parseFloat(ts.hours);
          return userAcc + (isNaN(hoursValue) ? 0 : hoursValue);
        }, 0);
        return taskAcc + userHours;
      }, 0);

      return {
        ...task,
        hours: totalHours,
      };
    });
  }, [tasksData]);

  const handleButtonClick = (type, row) => {
    setSelectedTask(row.original);
    queryClient.setQueryData(["taskId"], row.id, { staleTime: Infinity });

    if (type === "comments") {
      setDialogState((prev) => ({ ...prev, comments: true }));
    } else if (type === "attachment") {
      setDialogState((prev) => ({ ...prev, attachment: true }));
    } else if (type === "timesheet") {
      setDialogState((prev) => ({ ...prev, timesheet: true }));
    }
  };

  const handleDialogClose = () => {
    setDialogState(initialDialogState);
  };

  // Create custom hooks with notification handling
  const useCreateTaskWithNotification = () => {
    const mutation = useCreateTask();
    return {
      ...mutation,
      mutateAsync: async (variables) => {
        try {
          const result = await mutation.mutateAsync(variables);
          setSnackbar({
            open: true,
            message: "Task created successfully!",
            severity: "success",
          });
          return result;
        } catch (error) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to create task";
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: "error",
          });
          throw error;
        }
      },
    };
  };

  const useUpdateTaskWithNotification = () => {
    const mutation = useUpdateTask();
    return {
      ...mutation,
      mutateAsync: async (variables) => {
        try {
          const result = await mutation.mutateAsync(variables);
          setSnackbar({
            open: true,
            message: "Task updated successfully!",
            severity: "success",
          });
          return result;
        } catch (error) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to update task";
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: "error",
          });
          throw error;
        }
      },
    };
  };

  const useDeleteTaskWithNotification = () => {
    const mutation = useDeleteTask();
    return {
      ...mutation,
      mutateAsync: async (variables) => {
        try {
          const result = await mutation.mutateAsync(variables);
          setSnackbar({
            open: true,
            message: "Task deleted successfully!",
            severity: "success",
          });
          return result;
        } catch (error) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to delete task";
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: "error",
          });
          throw error;
        }
      },
    };
  };

  const hooks = {
    useCreateItem: () => useCreateTaskWithNotification(),
    useGetItems: useGetTasks,
    useUpdateItem: () => useUpdateTaskWithNotification(),
    useDeleteItem: () => useDeleteTaskWithNotification(),
  };

  const actions = {
    validateItem: validateTask,
    archiveItem: async ({ id, accessToken }) => {
      try {
        await archiveTaskMutation({ id, accessToken });
        setSnackbar({
          open: true,
          message: "Task archived successfully!",
          severity: "success",
        });
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to archive task";
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
        throw error;
      }
    },
  };

  const additionalProps = {
    enableSorting: true,
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        backgroundColor: "rgba(0,0,0,0)",
        width: "100%",
      },
    },
  };

  const isAdmin = true;
  // role === "admin" || role === "owner" || currentRole === "Project Manager";

  // Extract plant people for task member selection
  const plantPeople = selectedPlant?.people || [];
  const plantLeads = []; // Plants don't have project leads, but we can add plant admins if needed

  useEffect(() => {
    console.log("current role", role, username);
    if (selectedPlant?.people) {
      const userInPlant = selectedPlant.people.some((user) => {
        const userName =
          typeof user === "object" ? user.name || user.email : user;
        return userName === username;
      });
      if (userInPlant) {
        setCurrentRole("Plant Member");
      } else {
        setCurrentRole(null);
      }
    }
  }, [selectedPlant, username, role]);

  // Use useMemo to compute filtered tasks reactively
  const filteredTasks = useMemo(() => {
    // Handle undefined or null tasksWithHours
    if (!tasksWithHours) {
      return [];
    }

    // Ensure tasksWithHours is an array
    const tasksArray = Array.isArray(tasksWithHours) ? tasksWithHours : [];
    let tasks = tasksArray.filter((task) => !task.isArchived) || [];

    // Filter by fixedType if provided - this should be the first filter applied
    if (fixedType) {
      const normalizedFixedType = fixedType.toLowerCase().trim();
      tasks = tasks.filter((task) => {
        const taskType = task.type ? task.type.toLowerCase().trim() : "";
        return taskType === normalizedFixedType;
      });
      console.log(`Filtered by type "${fixedType}":`, tasks.length, "tasks");
    }

    const today = dayjs();

    // Apply additional filters based on filterType
    if (filterType === "dueToday") {
      tasks = tasks.filter(
        (task) => task.targetDate && dayjs(task.targetDate).isSame(today, "day")
      );
    } else if (filterType === "overdue") {
      tasks = tasks.filter(
        (task) =>
          task.targetDate &&
          dayjs(task.targetDate).isBefore(today, "day") &&
          task.progress < 100
      );
    } else if (filterType === "highPriority") {
      tasks = tasks.filter((task) => task.priority === "High");
    }

    console.log("filtered tasks", tasks.length, "tasks after all filters");
    return tasks;
  }, [filterType, tasksWithHours, fixedType]);

  const handleCommentDialogOpen = (row) => {
    console.log("row", row.original);
    setClickedProject(row.original.id);
    setCommentsDialogState(true);
  };

  const { totalEstimatedTime, averageProgress } = useMemo(() => {
    if (!Array.isArray(filteredTasks) || filteredTasks.length === 0) {
      return {
        totalEstimatedTime: 0,
        averageProgress: 0,
      };
    }

    let totalEst = 0;
    let totalProg = 0;
    let count = 0;

    filteredTasks.forEach((task) => {
      const est = parseFloat(task.estimatedTime);
      if (!isNaN(est)) {
        totalEst += est;
      }

      const prog = parseFloat(task.progress);
      if (!isNaN(prog)) {
        totalProg += prog;
        count += 1;
      }
    });

    return {
      totalEstimatedTime: totalEst,
      averageProgress: count ? totalProg / count : 0,
    };
  }, [filteredTasks]);

  return (
    <Box
      sx={{
        width: "100%",
        typography: "body1",
        overflow: "auto",
        margin: "16px 0",
      }}
    >
      {true ? (
        <>
          <Typography variant="h5">{selectedPlant.name}</Typography>
          <ToggleButtonGroup
            value={filterType}
            exclusive
            onChange={(event, filterType) => {
              setFilterType(filterType);
            }}
            aria-label="task filter"
            sx={{ marginBottom: 2 }}
          >
            <ToggleButton value="dueToday" aria-label="due today">
              Due Today
            </ToggleButton>
            <ToggleButton value="overdue" aria-label="overdue">
              Overdue
            </ToggleButton>
            <ToggleButton value="highPriority" aria-label="high priority">
              High Priority
            </ToggleButton>
          </ToggleButtonGroup>

          <>
            {fixedType && fixedType === "engineering" && (
              <Box
                sx={{
                  display: "flex",
                  gap: 4,
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="subtitle1">
                  Total Manhour:{" "}
                  <strong>{totalEstimatedTime.toFixed(2)}</strong>
                </Typography>
                <Typography variant="subtitle1">
                  Cummulative Progress:{" "}
                  <strong>{averageProgress.toFixed(1)}%</strong>
                </Typography>
              </Box>
            )}
            <EditableTable
              columns={taskListColumns(
                plantPeople,
                handleButtonClick,
                username,
                plantLeads,
                handleCommentDialogOpen,
                filteredTasks,
                fixedType,
                mrq,
              )}
              name={
                fixedType === "engineering"
                  ? "Task"
                  : fixedType === "fabrication"
                    ? "fabrication"
                    : "Action Item"
              }
              actions={actions}
              hooks={hooks}
              editMode="row"
              enableCreate={isAdmin ? true : false}
              enableEditing={isAdmin ? true : false}
              enableArchive={true}
              additionalProps={additionalProps}
              initialData={getInitialData(fixedType)}
              data={Array.isArray(filteredTasks) ? filteredTasks : []}
              accessToken={accessToken}
            />
          </>
        </>
      ) : (
        <>
          <Skeleton
            animation="wave"
            variant="text"
            sx={{ fontSize: "1rem" }}
            height={60}
            width={450}
          />
          <Skeleton animation="wave" variant="rectangular" height={"50vh"} />
        </>
      )}
      <CommentsDialog
        open={commentsModalState}
        handleClose={() => {
          setClickedProject("");
          setCommentsDialogState(false);
        }}
        parentId={clickedProject}
        parentType={"task"}
      />
      <TimesheetsDialog
        open={dialogState.timesheet}
        handleClose={handleDialogClose}
        task={selectedTask}
      />
      <ColoredSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}

